import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts"
import { MonthlyData } from "../../api/admin.api"

interface OverviewProps {
    data: MonthlyData[]
}



// Custom tooltip component
const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-popover/95 backdrop-blur-sm border border-border rounded-lg shadow-xl p-3 min-w-[120px]">
                <p className="text-sm font-semibold text-foreground mb-1">{label}</p>
                <p className="text-lg font-bold text-primary">
                    {Number(payload[0].value).toLocaleString('fr-FR')} HTG
                </p>
            </div>
        )
    }
    return null
}

export function Overview({ data }: OverviewProps) {
    if (!data || data.length === 0) {
        return (
            <div className="flex h-[350px] items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                Chargement des données...
            </div>
        )
    }

    // Find the max value to highlight it
    const maxValue = Math.max(...data.map(d => d.total || 0))

    return (
        <ResponsiveContainer width="100%" height={350}>
            <BarChart data={data} margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
                <defs>
                    {/* Gradient for bars */}
                    <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8b5cf6" stopOpacity={1} />
                        <stop offset="100%" stopColor="#6366f1" stopOpacity={0.8} />
                    </linearGradient>
                    {/* Highlight gradient for max bar */}
                    <linearGradient id="barGradientHighlight" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22d3ee" stopOpacity={1} />
                        <stop offset="100%" stopColor="#06b6d4" stopOpacity={0.9} />
                    </linearGradient>
                    {/* Subtle shadow filter */}
                    <filter id="barShadow" x="-20%" y="-20%" width="140%" height="140%">
                        <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#6366f1" floodOpacity="0.3" />
                    </filter>
                </defs>

                <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                    opacity={0.3}
                    vertical={false}
                />

                <XAxis
                    dataKey="month"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    dy={10}
                />

                <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) =>
                        value >= 1000000
                            ? `${(value / 1000000).toFixed(1)}M`
                            : value >= 1000
                                ? `${(value / 1000).toFixed(0)}K`
                                : `${value}`
                    }
                    width={50}
                />

                <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: 'hsl(var(--muted))', opacity: 0.3 }}
                />

                <Bar
                    dataKey="total"
                    radius={[8, 8, 0, 0]}
                    maxBarSize={60}
                    filter="url(#barShadow)"
                >
                    {data.map((entry, index) => (
                        <Cell
                            key={`cell-${index}`}
                            fill={entry.total === maxValue ? "url(#barGradientHighlight)" : "url(#barGradient)"}
                        />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    )
}
